import { IconButton } from "@shared/components/IconButton";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Calendar } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useValidationContext } from "../../context";
import { useMeterReadingConsumption, useMeterReadingDetail } from "../../hooks";
import { ConsumptionHistory } from "./ConsumptionHistory";
import ImageCard from "./ImageCard";
import { InfoCard, InfoItem } from "./InfoCard";
import { ValidationActionButtons } from "./ValidationActionButtons";
import { ViewToggle } from "./ViewToggle";

type ValidationDetailsViewProps = {
  readingId: string;
};

function ValidationDetailsView({ readingId }: ValidationDetailsViewProps) {
  const {
    meterRunId,
    setCodeId,
    nextReadingId,
    setNextReadingId,
    validatorId,
    duplicateReadingId,
  } = useValidationContext();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const isManualMeter = location.pathname.includes("manual-meter");

  const { data: detail } = useMeterReadingDetail({
    remoteUtilityId,
    meterRunId,
    meterReadingId: readingId,
    validatorId,
  });

  const nextReadingIdDetail = detail?.readingData?.previousDirectionId;
  const previousReadingId = detail?.readingData?.nextDirectionId;
  const meterNumber = detail?.meterNumber;

  useEffect(() => {
    if (detail) {
      setNextReadingId(nextReadingIdDetail);
    }
  }, [detail]);

  const { data: consumptionHistory } = useMeterReadingConsumption({
    remoteUtilityId,
    meterNumber,
  });

  const [selectedReadingId, setSelectedReadingId] = useState<any | null>(null);

  const updateSelectedReadingId = (newId: any) => {
    if (newId === "NA") return;
    setSelectedReadingId(newId);
    searchParams.set("readingId", String(newId));
    setSearchParams(searchParams, { replace: true });
  };

  const statusCode =
    detail?.exemptCode ?? detail?.readingData?.reasonCode ?? "";
  setCodeId(detail?.readingData?.currentStatusCode);
  const readingDetails: InfoItem[][] = [
    [
      {
        label: "Previous Reading",
        value: detail?.readingData?.previousReading,
        subtext: (
          <>
            <Calendar size={14} className="inline mr-1" />
            {detail?.readingData?.previousReadingDate}
            <p className="text-xs text-primary pt-1">
              Status: {detail?.readingData?.previousStatus}
            </p>
          </>
        ),
      },
    ],
    [
      {
        label: "Current Reading",
        value: detail?.readingData?.currentReading,
        subtext: (
          <>
            <Calendar size={14} className="inline mr-1" />
            {detail?.readingData?.currentReadingDate}
            <p className="text-xs text-primary pt-1">
              Status: {detail?.readingData?.currentStatus}
            </p>
          </>
        ),
      },
    ],
    [
      {
        label: "Consumption",
        value: detail?.readingData?.consumption,
        subtext: `Avg. ${detail?.readingData?.avgConsumption} per day`,
      },
    ],
    [
      {
        label: "Days",
        value: detail?.readingData?.totalDays,
        subtext: "Between readings",
      },
    ],
  ];

  const meterInfo: InfoItem[][] = [
    [
      { label: "Device Number", value: detail?.deviceNo },
      { label: "Installation", value: detail?.installedOn },
      { label: "Address", value: detail?.address },
    ],
    [
      { label: "Utility", value: detail?.productCode },
      { label: "Status", value: detail?.status },
    ],
  ];

  const meterReader: InfoItem[][] = [
    [
      {
        label: "Reader Name",
        value: detail?.readingData?.meterReaderDetails?.meterReader,
      },
      {
        label: "Contact",
        value: detail?.readingData?.meterReaderDetails?.contactNumber,
      },
      {
        label: "Reading Date",
        value: detail?.readingData?.currentReadingDate,
      },
    ],
    [
      {
        label: "Uploaded Date",
        value: detail?.readingData?.currentReadingDate,
      },
      {
        label: "Old Sequence",
        value: detail?.readingData?.oldSequence,
      },
      {
        label: "New Sequence",
        value: detail?.readingData?.newSequence,
      },
    ],
  ];

  const consumerInfo: InfoItem[][] = [
    [
      {
        label: "Consumer Name",
        value: `${detail?.firstName} ${detail?.lastName}`,
      },
      { label: "Account Number", value: detail?.consumerNo },
      { label: "Premise", value: detail?.premiseName },
    ],
    [
      { label: "Status", value: detail?.consumerStatus },
      { label: "Contact", value: detail?.contactNumber },
      { label: "Address", value: detail?.address },
    ],
  ];

  const suspiciousActivity: InfoItem[][] = [
    [
      {
        label: "Remark",
        value:
          detail?.readingData?.additionalData?.illegalActivityRemark ?? "N/A",
      },
    ],
    [
      {
        label: "Image",
        value:
          detail?.readingData?.additionalData?.illegalActivityPhoto ?? null,
        isLink: true,
      },
    ],
  ];

  const locationInfo: InfoItem[][] = [
    [
      { label: "Latitude", value: detail?.readingData?.latitude },
      { label: "Longitude", value: detail?.readingData?.longitude },
    ],
    [
      {
        label: "Distance Diff",
        value: detail?.readingData?.distance,
      },
      {
        label: "Route",
        value: detail?.routeDetails?.routeName,
      },
    ],
  ];

  const validatorNameItems: InfoItem[] = Array.isArray(
    detail?.assignedValidators,
  )
    ? detail.assignedValidators.map((v, index) => ({
        label: `Validator Name - #${index + 1}`,
        value: v.validatorName ?? "NA",
      }))
    : [
        {
          label: "Validator Name",
          value: "NA",
        },
      ];

  const validatorContactItems: InfoItem[] = Array.isArray(
    detail?.assignedValidators,
  )
    ? detail.assignedValidators.map((v, index) => ({
        label: `Validator Contact - #${index + 1}`,
        value: v.email || v.contactNo || "NA",
      }))
    : [
        {
          label: "Validator Contact",
          value: "NA",
        },
      ];

  const readCycleInfo: InfoItem[][] = [
    [
      {
        label: "Cycle Name",
        value: detail?.meterCycleDetails?.meterCycleName,
      },
      {
        label: "Route Name",
        value: detail?.routeDetails?.routeName,
      },
    ],
    [
      {
        label: "Scheduled Date",
        value: detail?.meterRunDetails?.startDate,
      },
      {
        label: "Completed Date",
        value: detail?.meterRunDetails?.endDate,
      },
    ],
    validatorNameItems,
    validatorContactItems,
  ];

  return (
    <>
      {!readingId ? (
        <div className="flex items-center justify-between pb-4 w-full">
          <p className="text-gray-500 italic text-center flex-1">
            Select a reading to view details.
          </p>
          <ViewToggle />
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center pb-4">
            <div className="flex gap-6">
              {/* Meter Number */}
              <div>
                <p className="text-gray-500 text-sm">Meter Number</p>
                <p className="text-black font-medium">
                  {detail?.meterNumber || "-"}
                </p>
              </div>

              {/* Assignment Status */}
              <div>
                <p className="text-gray-500 text-sm">Assignment Status</p>
                <div className="flex items-center gap-2">
                  <Badge variant="success">{detail?.status}</Badge>
                </div>
              </div>

              {statusCode && (
                <div>
                  <p className="text-gray-500 text-sm">Validation Code</p>
                  <div className="flex items-center gap-2">
                    <span className="text-black">{statusCode}</span>
                  </div>
                </div>
              )}
              {/* Validated By */}
              {detail?.validatedBy && (
                <div>
                  <p className="text-gray-500 text-sm">Validated By</p>
                  <p className="text-black font-medium">
                    {detail?.validatedBy?.validatorName || "-"}
                  </p>
                </div>
              )}
            </div>
            <ViewToggle />
          </div>

          <div className="flex justify-end pb-4">
            <ValidationActionButtons
              details={detail}
              isManualMeter={isManualMeter}
            />
          </div>

          <div className="flex justify-between pb-4">
            <IconButton
              label="Previous"
              onClick={() =>
                previousReadingId && updateSelectedReadingId(previousReadingId)
              }
              icon="ChevronLeft"
              disabled={!previousReadingId}
              variant="secondary"
            />
            <IconButton
              label="Next"
              onClick={() =>
                nextReadingId && updateSelectedReadingId(nextReadingId)
              }
              rightIcon="ChevronRight"
              disabled={!nextReadingId}
              variant="secondary"
            />
          </div>

          <div className="w-full flex gap-4">
            {!isManualMeter && (
              <span className="w-[45%]">
                <ImageCard
                  imageUrl={detail?.readingData?.currentReadingImage}
                  previousReadingId={detail?.readingData?.previousReadingId}
                />
              </span>
            )}
            <span
              className={`grid ${
                isManualMeter ? "grid-cols-2 w-full" : "grid-cols-2 flex-1"
              } gap-2`}
            >
              <InfoCard title="Reading Details" sections={readingDetails} />
              <InfoCard title="Meter Information" sections={meterInfo} />
              <InfoCard title="Meter Reader" sections={meterReader} />
              <InfoCard title="Consumer Info" sections={consumerInfo} />
              {!isManualMeter && (
                <InfoCard
                  title="Suspicious Activity"
                  sections={suspiciousActivity}
                />
              )}
              <InfoCard
                title="Read Cycle and Validator"
                sections={readCycleInfo}
              />
            </span>
          </div>

          <div className="flex gap-2 py-4">
            {!isManualMeter && (
              <InfoCard title="Location Information" sections={locationInfo} />
            )}
            <ConsumptionHistory
              data={
                consumptionHistory?.consumptionHistory
                  ?.slice(0, 5)
                  .map((item) => ({
                    month: item.billMonth,
                    value: item.totalConsumption,
                    color:
                      item.totalConsumption >
                      consumptionHistory?.avgConsumption * 1.5
                        ? "bg-red-500"
                        : "bg-blue-500",
                  })) ?? []
              }
            />
          </div>
        </>
      )}
    </>
  );
}

export default ValidationDetailsView;
